import React from 'react';
import { Image, TouchableOpacity, View } from 'react-native';

import { getProductHealthComplianceLocalValue } from '../../../common/utils/products/healthCompliance';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { formatCurrency } from '../../../common/utils/utils';
import { Text } from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ProductHealthCompliance } from '../product';
import PlanogramActionButton from './PlanogramActionButton';
import { resolveQuantityLabel, resolveSelectionLabel, showProductNameToast } from './PointOfSalePlanogramHelpers';

// One planogram selection in the list views; market items can be edited, added, moved and removed.
const PointOfSalePlanogramListItem = ({
	item,
	hideQuantity = false,
	itemList = [],
	itemIndex = -1,
	allowActions = true,
	allowEditDialog = true,
	tenant,
	healthComplianceLabel,
	isMarket,
	canModifyMarketItems,
	isMutatingPlanogram,
	actionItemId,
	openMarketItemDialog,
	createItemAfter,
	moveItemByOffset,
	confirmRemoveItem,
}) => {
	const styles = useThemedStyles(createStyles);
	const healthComplianceValue = tenant ? getProductHealthComplianceLocalValue(item.product, tenant) : null;
	const hasItemActions = Boolean(canModifyMarketItems && allowActions && item?.id);
	const canMoveUp = hasItemActions && itemIndex > 0 && !isMutatingPlanogram;
	const canMoveDown = hasItemActions && itemIndex < (itemList.length - 1) && !isMutatingPlanogram;
	const isActionDisabled = isMutatingPlanogram && actionItemId === item.id;

	return (
		<View style={styles.listItem}>
			<View style={[styles.listItemHeader, isMarket && styles.listItemHeaderMarket]}>
				{
					!isMarket && (
						<Text style={styles.listItemSelection}>{resolveSelectionLabel(item)}</Text>
					)
				}
				<View style={styles.listHealthContainer}>
					<ProductHealthCompliance product={item.product} />
					{
						Boolean(healthComplianceValue) && (
							<Text style={styles.listHealthText}>
								{`${healthComplianceLabel}: ${healthComplianceValue}`}
							</Text>
						)
					}
				</View>
			</View>
			<TouchableOpacity
				onPress={() => {
					if (canModifyMarketItems && allowEditDialog) {
						openMarketItemDialog(item);
						return;
					}
					showProductNameToast(item);
				}}
				activeOpacity={0.7}
				style={styles.listItemBody}
			>
				{
					item.product && (
						<Image
							style={styles.listItemImage}
							source={{ uri: getProductImageUrl(item.product) }}
						/>
					)
				}
				<Text style={styles.listItemName}>{item.product?.name || 'Empty selection'}</Text>
			</TouchableOpacity>
			{
				hideQuantity ? (
					<View style={styles.listItemFooter}>
						<View style={styles.valueBox}>
							<Text style={styles.listItemPrice}>{formatCurrency(item.price) || '$0'}</Text>
						</View>
					</View>
				) : (
					<View style={styles.listItemFooter}>
						<View style={styles.valueBox}>
							<Text style={styles.listItemQuantity}>{resolveQuantityLabel(item)}</Text>
						</View>
						<View style={styles.valueBox}>
							<Text style={styles.listItemPrice}>{formatCurrency(item.price) || '$0'}</Text>
						</View>
					</View>
				)
			}
			{
				hasItemActions && (
					<View style={styles.listItemActions}>
						<PlanogramActionButton
							label="Add below"
							disabled={isMutatingPlanogram}
							onPress={() => {
								void createItemAfter({
									anchorItem: item,
									planogramItemGroupId: item.planogramItemGroupId,
								});
							}}
						/>
						<PlanogramActionButton
							label="Move up"
							disabled={!canMoveUp}
							onPress={() => {
								void moveItemByOffset({
									item,
									itemList,
									itemIndex,
									offset: -1,
								});
							}}
						/>
						<PlanogramActionButton
							label="Move down"
							disabled={!canMoveDown}
							onPress={() => {
								void moveItemByOffset({
									item,
									itemList,
									itemIndex,
									offset: 1,
								});
							}}
						/>
						<PlanogramActionButton
							label="Remove"
							isDanger={true}
							disabled={isMutatingPlanogram || isActionDisabled}
							onPress={() => confirmRemoveItem(item)}
						/>
					</View>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	listItem: {
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		borderRadius: radius.card,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		backgroundColor: colors.raised,
		gap: space.sm,
	},
	listItemHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	listItemHeaderMarket: {
		justifyContent: 'flex-end',
	},
	listItemSelection: {
		...type.cardTitle,
		color: colors.text,
	},
	listHealthContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	listHealthText: {
		...type.caption,
		color: colors.textSecondary,
		marginLeft: space.xs,
	},
	listItemBody: {
		flexDirection: 'row',
		alignItems: 'center',
		minHeight: touch.min,
		gap: space.md,
	},
	listItemImage: {
		width: 44,
		height: 44,
		resizeMode: 'contain',
	},
	listItemName: {
		...type.rowTitle,
		color: colors.text,
		flex: 1,
	},
	listItemFooter: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: space.sm,
	},
	// Outlined value box, as on the pick list (price, now / par / capacity).
	valueBox: {
		borderWidth: 1.5,
		borderColor: colors.icon,
		borderRadius: radius.tag,
		paddingHorizontal: 6,
		paddingVertical: 2,
	},
	listItemQuantity: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	listItemPrice: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	listItemActions: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
});

export default PointOfSalePlanogramListItem;
